import { createSlice } from '@reduxjs/toolkit';
import {
  fetchRecruiterKpi,
  fetchRecruiterBreakdown,
  fetchDiversityBreakdown,
  fetchChannelBreakdown,
  fetchSenderBreakdown,
} from './actions';
import { topRecruiters, others } from '@/constants/reports-colors';

const constructSourcedData = (payload) => {
  const {
    table,
    lookups: { g1 } = {},
    misc: { other_count = 0 } = {},
  } = payload;
  const currTable = table?.filter((curr) => curr[0] === '2');
  const result = currTable?.reduce(
    (acc, curr, idx) => {
      const recruiterIdx = curr[2];
      const sourced = curr[4];
      const recruiter = g1[recruiterIdx];
      if (recruiter !== null) {
        acc.recruiters.push(recruiter?.name);
        acc.data.push({ y: sourced, color: topRecruiters[idx] });
      } else {
        acc.recruiters.push(`${other_count} Others`);
        acc.data.push({ y: sourced, color: others });
      }
      acc.sum = acc.sum + sourced;
      return acc;
    },
    { recruiters: [], data: [], sum: 0 }
  );
  return result;
};

const constructChartData = (
  payload,
  metricPosition = 0,
  countIdx = 5,
  top_by = 'count'
) => {
  const { table = [], lookups, headers = [] } = payload;
  const metric = table[metricPosition];
  const series = metric?.[0]?.reduce((acc, curr, idx) => {
    const [ds, _dt, _g1, g2] = curr; //eslint-disable-line
    const data = ds?.reduce((acc, curr) => {
      if (top_by === 'count') {
        acc.push([lookups?.dt[curr[1]], curr[countIdx]]);
      } else {
        const sentIdx = headers.indexOf('contacted');
        const rate =
          curr[sentIdx] > 0
            ? +((curr[countIdx] / curr[sentIdx]) * 100).toFixed(2)
            : 0;
        acc.push([lookups?.dt[curr[1]], rate]);
      }
      return acc;
    }, []);

    if (lookups?.g2?.[g2]) {
      acc.push({
        name: lookups?.g2?.[g2]?.name || lookups?.g2?.[g2] || 'Other',
        data: data,
        color: topRecruiters[idx],
      });
    } else {
      acc.push({
        name: 'Other',
        data: data,
        color: topRecruiters[idx],
      });
    }
    return acc;
  }, []);

  return { xAxis: lookups?.dt, series };
};

const initialState = {
  isLoading: true,
  chart: [],
  isRecruiterBreakdownLoading: true,
  recruiterBreakdown: [],
  genderBreakdown: [],
  isGenderBreakdownLoading: true,
  channelBreakDown: [],
  isChannelBreakDown: true,
  senderBreakDown: [],
  isSenderBreakDown: true,
};

const { reducer } = createSlice({
  name: 'kpi-candidate-sourced',
  initialState,

  extraReducers: {
    [fetchRecruiterKpi.pending]: (state) => {
      state.isLoading = true;
    },
    [fetchRecruiterKpi.fulfilled]: (state, { payload = {} }) => {
      state.isLoading = false;
      state.chart = constructSourcedData(payload);
    },

    [fetchRecruiterBreakdown.pending]: (state) => {
      state.isRecruiterBreakdownLoading = true;
    },

    [fetchRecruiterBreakdown.fulfilled]: (
      state,
      { payload = {}, meta = {} }
    ) => {
      const { arg: { filters: { top_by = 'count' } = {} } = {} } = meta;
      state.recruiterBreakdown = {
        opened: constructChartData(payload, 0, 6, top_by),
        replied: constructChartData(payload, 1, 7, top_by),
        interested: constructChartData(payload, 2, 8, top_by),
      };
      state.isRecruiterBreakdownLoading = false;
    },

    [fetchDiversityBreakdown.pending]: (state) => {
      state.isGenderBreakdownLoading = true;
    },

    [fetchDiversityBreakdown.fulfilled]: (
      state,
      { payload = {}, meta = {} }
    ) => {
      const { arg: { filters: { top_by = 'count' } = {} } = {} } = meta;
      state.genderBreakdown = {
        opened: constructChartData(payload, 0, 6, top_by),
        replied: constructChartData(payload, 1, 7, top_by),
        interested: constructChartData(payload, 2, 8, top_by),
      };
      state.isGenderBreakdownLoading = false;
    },

    [fetchChannelBreakdown.pending]: (state) => {
      state.isChannelBreakDown = true;
    },

    [fetchChannelBreakdown.fulfilled]: (state, { payload = {}, meta = {} }) => {
      const { arg: { filters: { top_by = 'count' } = {} } = {} } = meta;
      state.channelBreakDown = {
        opened: constructChartData(payload, 0, 5, top_by),
        replied: constructChartData(payload, 1, 6, top_by),
        interested: constructChartData(payload, 2, 7, top_by),
      };
      state.isChannelBreakDown = false;
    },

    [fetchSenderBreakdown.pending]: (state) => {
      state.isSenderBreakDown = true;
    },

    [fetchSenderBreakdown.fulfilled]: (state, { payload = {}, meta = {} }) => {
      const { arg: { filters: { top_by = 'count' } = {} } = {} } = meta;
      state.senderBreakDown = {
        opened: constructChartData(payload, 0, 5, top_by),
        replied: constructChartData(payload, 1, 6, top_by),
        interested: constructChartData(payload, 2, 7, top_by),
      };
      state.isSenderBreakDown = false;
    },
  },
});

export default reducer;
